import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import moment from 'moment'
import { Card } from '@components/v2/card'
import { Loader } from '@components/v2/loader'
import { getSubmissionsUsers } from '@global/api'
import { ISubmissionsUsersResponse } from '@global/api/types'
import { Tooltip } from '@components/tooltip/tooltip'
import { InformationCircle } from './informationCircle'
import { TOOLTIP_INFO } from '../constants'
import { DEFAULT_ERROR_REPORTS_QUERY_PARAMS } from '../queries/useReportsErrorRateQuery'
import { t } from '@lingui/core/macro'

interface IUserActivityData {
  data: any
  maxActive: number
  maxInactive: number
}

const SIX_MONTHS_AGO = moment().subtract(5, 'months').startOf('month').valueOf()

export const UserActivityChartCard = ({ isLoading }) => {
  const [activeChart, setActiveChart] = useState('all')

  const { data: userActivityData } = useQuery({
    queryKey: [
      'roi-submissions-user-activity',
      DEFAULT_ERROR_REPORTS_QUERY_PARAMS.numberOfDaysToFilter,
    ],
    queryFn: ({ signal }) => getSubmissionsUsers({ signal, ...DEFAULT_ERROR_REPORTS_QUERY_PARAMS }),
    refetchOnWindowFocus: false,
    select: (data: ISubmissionsUsersResponse[]): IUserActivityData => {
      const transformedData = data
        ?.filter(item => {
          const itemDate = moment(item.date)
          return itemDate.valueOf() >= SIX_MONTHS_AGO
        })
        .map(item => ({
          active: item.active_users,
          inactive: item.total_users - item.active_users, // Calculate inactive users
          ...item,
        }))

      const result = data.reduce(
        (max, current) => (current.active_users > max.active_users ? current : max),
        { active_users: 0, total_users: 0 },
      )

      return {
        data: transformedData,
        maxActive: result.active_users,
        maxInactive: result.total_users - result.active_users,
      }
    },
  })

  const { data, maxActive = 0, maxInactive = 0 } = userActivityData || {}

  return (
    <Card variant="primary">
      <Card.Header className="flex pl-[20px] justify-between border-b border-b-cv-blue-200">
        <div className="flex items-center gap-3">
          <Card.Title className="text-cv-blue-700 text-base font-bold">
            {t`User engagement with submissions`}
          </Card.Title>
          <Tooltip
            title={<p className="text-sm font-bold">{TOOLTIP_INFO.ENGAGEMENT.TITLE}</p>}
            description={<span className="text-xs">{TOOLTIP_INFO.ENGAGEMENT.DESCRIPTION}</span>}
            mode="dark"
            className="w-[383px] p-[16px]"
          >
            <InformationCircle />
          </Tooltip>
        </div>

        <div className="flex">
          {[
            { key: 'all', label: 'ALL', count: maxActive + maxInactive },
            { key: 'active', label: 'Active', count: maxActive },
            { key: 'inactive', label: 'Inactive', count: maxInactive },
          ].map(chart => (
            <Card.HeaderButton
              active={activeChart === chart.key}
              onClick={() => {
                setActiveChart(chart.key)
              }}
              label={chart.label}
              count={chart.count}
              isLoading={isLoading}
              key={chart.key}
            />
          ))}
        </div>
      </Card.Header>
      <Card.Content>
        {isLoading ? (
          <div className="flex justify-center items-center p-6 h-[200px]">
            <Loader className="h-12 w-12" />
          </div>
        ) : (
          <Card.Chart
            config={{
              active: {
                label: 'Active',
                color: 'hsla(204, 100%, 70%, 1)',
              },
              inactive: {
                label: 'Inactive',
                color: 'hsla(198, 23%, 83%, 1)',
              },
            }}
            data={data}
            activeChart={activeChart}
          />
        )}
      </Card.Content>
    </Card>
  )
}
